/**
 * ProductMarketingElements
 * Manages marketing elements on product pages:
 * - Low stock warnings (updates on variant change)
 * - Units sold counter (localStorage persistence)
 * - Live viewers simulator (real-time updates)
 */

if (!customElements.get('product-form')) {
  console.warn('ProductMarketingElements: product-form custom element not found');
}

class ProductMarketingElements {
  constructor(config) {
    this.config = {
      sectionId: config.sectionId,
      productId: config.productId,
      enableLowStock: config.enableLowStock !== false,
      enableUnitsSold: config.enableUnitsSold !== false,
      enableLiveViewers: config.enableLiveViewers !== false,
      lowStockThreshold: config.lowStockThreshold || 10,
      unitsSoldMin: config.unitsSoldMin || 150,
      unitsSoldMax: config.unitsSoldMax || 500,
      unitsSoldIncrementMin: config.unitsSoldIncrementMin || 1,
      unitsSoldIncrementMax: config.unitsSoldIncrementMax || 5,
      liveViewersMin: config.liveViewersMin || 2,
      liveViewersMax: config.liveViewersMax || 15,
      liveViewersVariance: config.liveViewersVariance || 3,
      enableUrgencyTimer: config.enableUrgencyTimer === true,
      urgencyTimerMode: config.urgencyTimerMode || 'static',
      urgencyTimerDate: config.urgencyTimerDate || '2026-12-31 23:59',
      urgencyTimerMinutes: config.urgencyTimerMinutes || 30,
      urgencyTimerResetHours: config.urgencyTimerResetHours || 24,
      currentVariant: config.currentVariant || {}
    };

    // DOM Elements
    this.container = document.getElementById(`product-marketing-${this.config.sectionId}`);
    if (!this.container) {
      console.warn('ProductMarketingElements: Container not found');
      return;
    }

    this.lowStockElement = document.getElementById(`low-stock-${this.config.sectionId}`);
    this.unitsSoldElement = document.getElementById(`units-sold-${this.config.sectionId}`);
    this.liveViewersElement = document.getElementById(`live-viewers-${this.config.sectionId}`);
    this.urgencyTimerElement = document.getElementById(`urgency-timer-${this.config.sectionId}`);

    // State
    this.currentViewers = 0;
    this.viewersInterval = null;
    this.unitsSoldCount = 0;
    this._sessionCounter = null; // Fallback for Safari private mode
    this.urgencyTimerInterval = null;
    this.urgencyTimerEndTime = null;

    // Initialize
    this.init();
  }

  init() {
    // Subscribe to variant change events
    if (this.config.enableLowStock) {
      this.subscribeToVariantChanges();
    }

    // Initialize units sold counter
    if (this.config.enableUnitsSold && this.unitsSoldElement) {
      this.initUnitsSold();
    }

    // Initialize live viewers
    if (this.config.enableLiveViewers && this.liveViewersElement) {
      this.initLiveViewers();
    }

    // Initialize urgency timer
    if (this.config.enableUrgencyTimer && this.urgencyTimerElement) {
      this.initUrgencyTimer();
    }
  }

  /**
   * Subscribe to variant change events using Shopify's pub/sub system
   */
  subscribeToVariantChanges() {
    if (!this.lowStockElement) return;

    // Try to use Shopify's pub/sub system
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.html) {
          // Use the fresh HTML from the server which has accurate inventory data
          this.updateLowStockFromHtml(event.data.html);
        } else if (!event.data || !event.data.variant) {
          // Variant is null/unavailable - hide low stock warning
          this.lowStockElement.classList.add('hidden');
        }
      });
    } else {
      // Fallback: listen to form changes
      const productForm = this.container.closest('product-form') ||
                          document.querySelector(`product-form[data-section-id="${this.config.sectionId}"]`);

      if (productForm) {
        const variantInput = productForm.querySelector('.product-variant-id');
        if (variantInput) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                // Variant changed, fetch new data
                this.fetchVariantData(mutation.target.value);
              }
            });
          });

          observer.observe(variantInput, { attributes: true });
        }
      }
    }
  }

  /**
   * Update low stock warning from fresh HTML response
   */
  updateLowStockFromHtml(html) {
    if (!this.lowStockElement) return;

    // Find the low-stock element in the fresh HTML
    const sourceLowStock = html.getElementById(`low-stock-${this.config.sectionId}`);

    if (sourceLowStock) {
      // Copy the classes (hidden, critical, etc.)
      const isHidden = sourceLowStock.classList.contains('hidden');
      const isCritical = sourceLowStock.classList.contains('critical');

      // Update visibility
      if (isHidden) {
        this.lowStockElement.classList.add('hidden');
      } else {
        this.lowStockElement.classList.remove('hidden');
      }

      // Update critical state
      if (isCritical) {
        this.lowStockElement.classList.add('critical');
      } else {
        this.lowStockElement.classList.remove('critical');
      }

      // Update the text content
      const sourceText = sourceLowStock.querySelector('.low-stock-text');
      const destText = this.lowStockElement.querySelector('.low-stock-text');
      if (sourceText && destText) {
        destText.innerHTML = sourceText.innerHTML;
      }
    } else {
      // Element not found in fresh HTML - hide it
      this.lowStockElement.classList.add('hidden');
    }
  }

  /**
   * Fetch variant data from Shopify API
   */
  async fetchVariantData(variantId) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${this.config.productId}.js`);
      const productData = await response.json();
      const variant = productData.variants.find(v => v.id === parseInt(variantId));

      if (variant) {
        this.updateLowStock({
          id: variant.id,
          inventory_quantity: variant.inventory_quantity,
          inventory_management: variant.inventory_management,
          inventory_policy: variant.inventory_policy
        });
      }
    } catch (error) {
      console.warn('ProductMarketingElements: Error fetching variant data', error);
    }
  }

  /**
   * Update low stock warning based on variant data
   */
  updateLowStock(variant) {
    if (!this.lowStockElement) return;

    const inventory = variant.inventory_quantity || 0;
    const inventoryManagement = variant.inventory_management;
    const threshold = this.config.lowStockThreshold;

    // Only show if Shopify manages inventory and stock is low
    const shouldShow = inventoryManagement === 'shopify' &&
                       inventory > 0 &&
                       inventory <= threshold;

    if (shouldShow) {
      // Update text
      const textElement = this.lowStockElement.querySelector('.low-stock-text');
      if (textElement) {
        // Idioma base: la tienda sirve pt-PT y las claves de abajo son de dos letras.
        const locale = (document.documentElement.lang || 'en').split('-')[0];
        // Si la variante se sigue vendiendo sin stock, estas unidades son las
        // unicas que salen ya. Mismo criterio que el servidor.
        // Este camino es solo el respaldo: el normal copia el HTML del servidor.
        // El JSON publico de producto NO trae inventory_policy (llega undefined),
        // asi que aqui se cae al texto sin expres. Es lo correcto: antes de
        // prometer una salida rapida que no podemos comprobar, no se promete.
        const expressStock = variant.inventory_policy === 'continue';
        textElement.innerHTML = this.getLowStockText(inventory, locale, expressStock);
      }

      // Check if critical (≤5 or ≤50% of threshold)
      const isCritical = inventory <= 5 || inventory <= (threshold / 2);

      if (isCritical) {
        this.lowStockElement.classList.add('critical');
      } else {
        this.lowStockElement.classList.remove('critical');
      }

      // Show element
      this.lowStockElement.classList.remove('hidden');
    } else {
      // Hide element
      this.lowStockElement.classList.add('hidden');
    }
  }

  /**
   * Get localized low stock text
   */
  getLowStockText(inventory, locale, expressStock = false) {
    const texts = expressStock ? {
      'es': `<strong>¡Solo quedan ${inventory} con salida exprés!</strong> Stock limitado`,
      'de': `<strong>Nur noch ${inventory} mit schnellem Versand!</strong> Begrenzter Bestand`,
      'fr': `<strong>Plus que ${inventory} en expédition express&nbsp;!</strong> Stock limité`,
      'it': `<strong>Solo ${inventory} con spedizione rapida!</strong> Stock limitato`,
      'pt': `<strong>Apenas ${inventory} com envio rápido!</strong> Stock limitado`,
      'en': `<strong>Only ${inventory} with express dispatch!</strong> Limited stock`
    } : {
      'es': `<strong>¡Solo quedan ${inventory}!</strong> Stock limitado`,
      'de': `<strong>Nur noch ${inventory} verfügbar!</strong> Begrenzter Bestand`,
      'fr': `<strong>Plus que ${inventory} disponible&nbsp;!</strong> Stock limité`,
      'it': `<strong>Solo ${inventory} rimasti!</strong> Stock limitato`,
      'pt': `<strong>Apenas ${inventory} restantes!</strong> Stock limitado`,
      'en': `<strong>Only ${inventory} left!</strong> Limited stock`
    };

    return texts[locale] || texts['en'];
  }

  /**
   * Initialize units sold counter
   */
  initUnitsSold() {
    const data = this.getUnitsSoldData();
    this.unitsSoldCount = data.count;

    // Animate counter
    this.animateCounter(this.unitsSoldCount);
  }

  /**
   * Get units sold data from localStorage
   */
  getUnitsSoldData() {
    const key = `units_sold_${this.config.productId}`;
    const currentMonth = this.getCurrentMonth();

    try {
      const stored = localStorage.getItem(key);

      if (stored) {
        const data = JSON.parse(stored);

        // Check if we're in a new month (reset counter)
        if (data.month !== currentMonth) {
          return this.createNewUnitsSoldData(key, currentMonth);
        }

        // Increment counter for returning visit
        const incrementRange = this.config.unitsSoldIncrementMax - this.config.unitsSoldIncrementMin + 1;
        data.count += Math.floor(Math.random() * incrementRange) + this.config.unitsSoldIncrementMin;
        data.lastUpdated = new Date().toISOString();

        // Save updated data
        localStorage.setItem(key, JSON.stringify(data));

        return data;
      } else {
        // First visit - create new data
        return this.createNewUnitsSoldData(key, currentMonth);
      }
    } catch (error) {
      // localStorage blocked (Safari private mode, etc.)
      console.warn('ProductMarketingElements: localStorage not available, using session fallback');

      if (this._sessionCounter === null) {
        const range = this.config.unitsSoldMax - this.config.unitsSoldMin + 1;
        this._sessionCounter = Math.floor(Math.random() * range) + this.config.unitsSoldMin;
      }

      return {
        count: this._sessionCounter,
        month: currentMonth,
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Create new units sold data
   */
  createNewUnitsSoldData(key, month) {
    const range = this.config.unitsSoldMax - this.config.unitsSoldMin + 1;
    const count = Math.floor(Math.random() * range) + this.config.unitsSoldMin;
    const data = {
      count: count,
      month: month,
      lastUpdated: new Date().toISOString()
    };

    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // localStorage blocked
      this._sessionCounter = count;
    }

    return data;
  }

  /**
   * Get current month in format "YYYY-M"
   */
  getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth()}`;
  }

  /**
   * Animate counter from 0 to target value
   */
  animateCounter(targetValue) {
    const counterElement = this.unitsSoldElement.querySelector('.units-sold-number');
    if (!counterElement) return;

    const duration = 1000; // 1 second
    const steps = 60;
    const stepDuration = duration / steps;
    const increment = targetValue / steps;
    let currentValue = 0;

    counterElement.setAttribute('data-target', targetValue);
    counterElement.classList.add('animating');

    const timer = setInterval(() => {
      currentValue += increment;

      if (currentValue >= targetValue) {
        currentValue = targetValue;
        counterElement.textContent = Math.floor(currentValue);
        clearInterval(timer);

        // Remove animation class after animation completes
        setTimeout(() => {
          counterElement.classList.remove('animating');
        }, 100);
      } else {
        counterElement.textContent = Math.floor(currentValue);
      }
    }, stepDuration);
  }

  /**
   * Initialize live viewers counter
   */
  initLiveViewers() {
    // Set initial random value
    const range = this.config.liveViewersMax - this.config.liveViewersMin + 1;
    this.currentViewers = Math.floor(Math.random() * range) + this.config.liveViewersMin;
    this.updateViewersDisplay();

    // Start updating
    this.scheduleViewersUpdate();
  }

  /**
   * Update viewers display
   */
  updateViewersDisplay() {
    const countElement = this.liveViewersElement.querySelector('.live-viewers-count');
    if (countElement) {
      // Smooth transition
      countElement.style.transition = 'opacity 0.3s ease';
      countElement.style.opacity = '0';

      setTimeout(() => {
        countElement.textContent = this.currentViewers;
        countElement.style.opacity = '1';
      }, 150);
    }
  }

  /**
   * Schedule next viewers update
   */
  scheduleViewersUpdate() {
    // Clear existing interval
    if (this.viewersInterval) {
      clearTimeout(this.viewersInterval);
    }

    // Random interval between 1-10 seconds
    const nextUpdate = (Math.floor(Math.random() * 10) + 1) * 1000;

    this.viewersInterval = setTimeout(() => {
      this.updateViewers();
      this.scheduleViewersUpdate(); // Schedule next update
    }, nextUpdate);
  }

  /**
   * Update viewers count with random variance
   */
  updateViewers() {
    // Generate a random change of at least ±1, never 0
    const changeAmount = Math.floor(Math.random() * this.config.liveViewersVariance) + 1;
    const direction = Math.random() < 0.5 ? -1 : 1;
    let newViewers = this.currentViewers + (changeAmount * direction);

    // Keep within bounds from config
    newViewers = Math.max(this.config.liveViewersMin, Math.min(this.config.liveViewersMax, newViewers));

    // If we hit a boundary and the value didn't change, try opposite direction
    if (newViewers === this.currentViewers) {
      newViewers = this.currentViewers + (changeAmount * -direction);
      newViewers = Math.max(this.config.liveViewersMin, Math.min(this.config.liveViewersMax, newViewers));
    }

    this.currentViewers = newViewers;
    this.updateViewersDisplay();
  }

  /**
   * Initialize urgency timer
   */
  initUrgencyTimer() {
    const mode = this.config.urgencyTimerMode;

    if (mode === 'date') {
      // Date mode: countdown to specific date
      this.initDateModeTimer();
    } else {
      // Static mode: session-based timer
      this.initStaticModeTimer();
    }
  }

  /**
   * Initialize date mode timer (countdown to specific date)
   */
  initDateModeTimer() {
    // Parse target date
    const targetDate = this.parseDate(this.config.urgencyTimerDate);

    if (!targetDate || targetDate <= new Date()) {
      // Invalid date or date in past - hide timer
      this.urgencyTimerElement.classList.add('hidden');
      return;
    }

    this.urgencyTimerEndTime = targetDate;
    this.updateUrgencyTimerDisplay();

    // Update every second
    this.urgencyTimerInterval = setInterval(() => {
      const now = new Date();
      if (now >= this.urgencyTimerEndTime) {
        // Timer expired
        this.urgencyTimerElement.classList.add('hidden');
        clearInterval(this.urgencyTimerInterval);
      } else {
        this.updateUrgencyTimerDisplay();
      }
    }, 1000);
  }

  /**
   * Initialize static mode timer (session-based countdown)
   */
  initStaticModeTimer() {
    const storageKey = `urgency_timer_${this.config.productId}`;

    try {
      const stored = localStorage.getItem(storageKey);
      const now = new Date();

      if (stored) {
        const data = JSON.parse(stored);
        const startTime = new Date(data.startTime);
        const endTime = new Date(data.endTime);
        const resetTime = new Date(data.resetTime);

        // Check if timer is still running
        if (now < endTime) {
          // Timer still running - continue from where we left off
          this.urgencyTimerEndTime = endTime;
          this.urgencyTimerElement.classList.remove('hidden');
          this.startStaticTimer();
          return;
        }

        // Check if we're in reset period (after timer expired, before reset time)
        if (now >= endTime && now < resetTime) {
          // Still in reset period - hide timer
          this.urgencyTimerElement.classList.add('hidden');

          // Schedule showing timer when reset period ends
          const resetDelay = resetTime - now;
          if (resetDelay > 0 && resetDelay < 48 * 60 * 60 * 1000) { // Max 48 hours
            setTimeout(() => {
              this.resetStaticTimer(storageKey);
            }, resetDelay);
          }
          return;
        }

        // Reset period ended - start new timer
        this.resetStaticTimer(storageKey);
      } else {
        // First time - create new timer
        this.resetStaticTimer(storageKey);
      }
    } catch (error) {
      console.warn('ProductMarketingElements: localStorage not available for urgency timer');
      // Fallback: just start timer without persistence
      const now = new Date();
      const durationMs = this.config.urgencyTimerMinutes * 60 * 1000;
      this.urgencyTimerEndTime = new Date(now.getTime() + durationMs);
      this.urgencyTimerElement.classList.remove('hidden');
      this.startStaticTimer();
    }
  }

  /**
   * Reset and start static timer
   */
  resetStaticTimer(storageKey) {
    const now = new Date();
    const durationMs = this.config.urgencyTimerMinutes * 60 * 1000;
    const endTime = new Date(now.getTime() + durationMs);
    const resetHours = this.config.urgencyTimerResetHours;
    const resetTime = new Date(endTime.getTime() + (resetHours * 60 * 60 * 1000));

    const timerData = {
      startTime: now.toISOString(),
      endTime: endTime.toISOString(),
      resetTime: resetTime.toISOString()
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(timerData));
    } catch (error) {
      // localStorage unavailable
    }

    this.urgencyTimerEndTime = endTime;
    this.urgencyTimerElement.classList.remove('hidden');
    this.startStaticTimer();
  }

  /**
   * Start static timer countdown
   */
  startStaticTimer() {
    this.updateUrgencyTimerDisplay();

    // Update every second
    this.urgencyTimerInterval = setInterval(() => {
      const now = new Date();
      if (now >= this.urgencyTimerEndTime) {
        // Timer expired
        this.urgencyTimerElement.classList.add('hidden');
        clearInterval(this.urgencyTimerInterval);

        // Schedule reset
        const storageKey = `urgency_timer_${this.config.productId}`;
        const resetHours = this.config.urgencyTimerResetHours;
        const resetMs = resetHours * 60 * 60 * 1000;

        setTimeout(() => {
          this.resetStaticTimer(storageKey);
        }, resetMs);
      } else {
        this.updateUrgencyTimerDisplay();
      }
    }, 1000);
  }

  /**
   * Update urgency timer display
   */
  updateUrgencyTimerDisplay() {
    if (!this.urgencyTimerEndTime) return;

    const now = new Date();
    const diff = this.urgencyTimerEndTime - now;

    if (diff <= 0) {
      return;
    }

    // Calculate time components
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Update display
    const daysSegment = this.urgencyTimerElement.querySelector('.urgency-timer-days');
    const hoursSegment = this.urgencyTimerElement.querySelector('.urgency-timer-hours');
    const minutesSegment = this.urgencyTimerElement.querySelector('.urgency-timer-minutes');
    const secondsSegment = this.urgencyTimerElement.querySelector('.urgency-timer-seconds');

    // Show/hide days
    if (days > 0) {
      daysSegment.style.display = 'inline-flex';
      daysSegment.querySelector('[data-unit="days"]').textContent = days;
    } else {
      daysSegment.style.display = 'none';
    }

    // Show/hide hours
    if (hours > 0 || days > 0) {
      hoursSegment.style.display = 'inline-flex';
      hoursSegment.querySelector('[data-unit="hours"]').textContent = hours;
    } else {
      hoursSegment.style.display = 'none';
    }

    // Always show minutes and seconds
    minutesSegment.querySelector('[data-unit="minutes"]').textContent = minutes;
    secondsSegment.querySelector('[data-unit="seconds"]').textContent = seconds;
  }

  /**
   * Parse date string in format "YYYY-MM-DD HH:MM"
   */
  parseDate(dateString) {
    try {
      // Handle format: "YYYY-MM-DD HH:MM"
      const parts = dateString.trim().split(' ');
      if (parts.length !== 2) return null;

      const dateParts = parts[0].split('-');
      const timeParts = parts[1].split(':');

      if (dateParts.length !== 3 || timeParts.length !== 2) return null;

      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JS months are 0-indexed
      const day = parseInt(dateParts[2]);
      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);

      const date = new Date(year, month, day, hour, minute, 0);

      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      return null;
    }
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.viewersInterval) {
      clearTimeout(this.viewersInterval);
      this.viewersInterval = null;
    }

    if (this.urgencyTimerInterval) {
      clearInterval(this.urgencyTimerInterval);
      this.urgencyTimerInterval = null;
    }
  }
}

// Make class globally available
window.ProductMarketingElements = ProductMarketingElements;
